import { type Ref, ref, computed } from "vue";
import apiGraph from "@/helpers/api/apiGraph";

export const useGroupComposable = () => {
  const isMemberOfGroupMap: Ref<Record<string, Record<string, boolean>>> = ref({});
  const isOwnerOfGroupMap: Ref<Record<string, Record<string, boolean>>> = ref({});

  // Function-getters
  const isUserMemberOfGroup = computed(
    () =>
      (userId: string, groupId: string): boolean | undefined =>
        isMemberOfGroupMap.value[userId] ? isMemberOfGroupMap.value[userId][groupId] : undefined
  );

  const isUserOwnerOfGroup = computed(
    () =>
      (userId: string, groupId: string): boolean | undefined =>
        isOwnerOfGroupMap.value[userId] ? isOwnerOfGroupMap.value[userId][groupId] : undefined
  );

  const isMemberOfGroup = (userId: string, groupId: string, isMember: boolean) => {
    if (!isMemberOfGroupMap.value[userId]) {
      isMemberOfGroupMap.value[userId] = {};
    }
    isMemberOfGroupMap.value[userId][groupId] = isMember;
  };

  const isOwnerOfGroup = (userId: string, groupId: string, isOwner: boolean) => {
    if (!isOwnerOfGroupMap.value[userId]) {
      isOwnerOfGroupMap.value[userId] = {};
    }
    isOwnerOfGroupMap.value[userId][groupId] = isOwner;
  };

  /**
   * Checks if user is member of group in graph
   */
  const isMembersOfGroup = async (userId: string, groupId: string): Promise<boolean> => {
    const cached = isUserMemberOfGroup.value(userId, groupId);
    if (cached !== undefined) {
      return cached;
    }

    try {
      let response = await apiGraph.get(`/groups/${groupId}/members`);

      if (response.data.value.some((m: { id: string }) => m.id === userId)) {
        isMemberOfGroup(userId, groupId, true);
        return true;
      }

      while (response.data && response.data["@odata.nextLink"]) {
        response = await apiGraph.get(response.data["@odata.nextLink"]);

        if (response.data.value.some((m: { id: string }) => m.id === userId)) {
          isMemberOfGroup(userId, groupId, true);
          return true;
        }
      }
    } catch {
      isMemberOfGroup(userId, groupId, false);
      return false;
    }
    isMemberOfGroup(userId, groupId, false);
    return false;
  };

  /**
   * Checks if user is owner of group in graph
   */
  const isOwnersOfGroup = async (userId: string, groupId: string): Promise<boolean> => {
    const cached = isUserOwnerOfGroup.value(userId, groupId);
    if (cached !== undefined) {
      return cached;
    }

    try {
      let response = await apiGraph.get(`/groups/${groupId}/owners`);

      if (response.data.value.some((m: { id: string }) => m.id === userId)) {
        isOwnerOfGroup(userId, groupId, true);
        return true;
      }

      while (response.data && response.data["@odata.nextLink"]) {
        response = await apiGraph.get(response.data["@odata.nextLink"]);
        if (response.data.value.some((m: { id: string }) => m.id === userId)) {
          isOwnerOfGroup(userId, groupId, true);
          return true;
        }
      }
    } catch {
      isOwnerOfGroup(userId, groupId, false);
      return false;
    }
    isOwnerOfGroup(userId, groupId, false);
    return false;
  };

  return {
    isMemberOfGroupMap,
    isOwnerOfGroupMap,
    isMemberOfGroup,
    isOwnerOfGroup,
    isMembersOfGroup,
    isOwnersOfGroup,
    isUserMemberOfGroup,
    isUserOwnerOfGroup
  };
};
